/*
 * Copyright (c) 2020 NOOGA DEVELOPMENT, All rights reserved.
 * Author Raman Kremko <raman.kremko@noogadev.com>
 */

import { factoryT, factoryTBuilder, fields } from 'factory-t';
import {
    AuditDto,
    CASH_BOXES_STATUSES,
    CashBoxAlertsDto,
    CashBoxAuditHistoryItemDto,
    CashBoxDetailsDto,
    CashBoxDetailsForMap,
    CashBoxLastDocument,
    CashBoxListItemDto,
    CashboxLocationData,
    CashBoxPoint,
    CashBoxReplacementDto,
    CashBoxStateDto,
    CashBoxStatusDto,
    CashBoxTimeDeltaDto,
    CashBoxTypes,
    CheckSumsHistoryItemDto,
    LocationsListItem,
} from './cashBoxesClient.typings';
import { legalAddressFactory } from '../operators/operatorsFactories';
import { STORE_STATUSES, StoreType } from '../stores/storesClientTypings';
import { COMPANIES_STATUSES } from '../companies/companiesClient.typings';
import { Currency, RegistryTurnoversByPeriodDto } from '../apiCommon.typings';
import { turnoversPartialDtoFactory } from '../reports/reportsFactories';
import moment from 'moment';
import { VersionStatus } from '../cashRegisterModels/cashRegisterModelsClient.typings';
import { StoreLowerActivity, StoreMainActivity, StoreMiddleActivity } from '../shortLists/shortListsClient.typings';

export const cashBoxListItemFactory = factoryTBuilder<CashBoxListItemDto>({
    taxNumber: (ctx) => `7323432${ctx.index}`,
    companyTaxNumber: (ctx) => `0200000711${ctx.index}`,
    storeTaxNumber: (ctx) => `6454555${ctx.index}`,
    cashBoxType: fields.sequence([
        CashBoxTypes.PHYSICAL,
        CashBoxTypes.ECOMM,
        CashBoxTypes.VIRTUAL,
        CashBoxTypes.SOFTWARE,
    ]),
    storeName: fields.string(),
    storeType: fields.sequence(Object.values(StoreType)),
    storeAddress: legalAddressFactory.item(),
    storeFullAddress: 'Region11 Locale1 City5region11 Street1city84 building 1',
    online: fields.sequence([true, false, true, true, true, false, true]),
    status: fields.sequence([...CASH_BOXES_STATUSES]),
    validTime: fields.sequence([false, true, false, false, true, true, false, true]),
    timeDelta: fields.sequence([-200, 400, 556, null, -384, null, null]),
    firmwareVersion: fields.sequence(['2.6.1-werw', '2.32.0', null, undefined]),
    temporary: fields.sequence([true, false, true, false, false]),
    taxationTypes: ['VAT', 'INCOME', 'PROFIT', 'SIMPLIFIED'],
    storeTaxAuthority: (ctx) => `TaxAuthority-${ctx.index}`,
    cashRegisterFactoryNumber: (ctx) => `4637298-${ctx.index}`,
    fiscalDriverVersion: fields.sequence(['1.6.1-wa', '1.32.0-asv', null, undefined]),
})
    .setFieldFactory('storeName', (ctx) => `Store${ctx.inject('storeTaxNumber')} name`)
    .factory();

export const cashBoxDetailsFactory = factoryTBuilder<CashBoxDetailsDto>({
    taxNumber: (ctx) => `7323432${ctx.index}`,
    storeTaxNumber: (ctx) => 6454555 + ctx.index,
    operatorTaxNumber: (ctx) => 453465 + ctx.index,
    operatorName: fields.string(),
    storeName: fields.string(),
    storeType: fields.sequence(Object.values(StoreType)),
    storeAddress: legalAddressFactory.item(),
    storeFullAddress: 'Region11 Locale1 City5region11 Street1city84 building 1',
    online: fields.sequence([true]),
    validTime: fields.sequence([false, true, false, false, true, true, false, true]),
    timeDelta: fields.sequence([-200, 400, 556, null, -384, null, null]),
    firmwareVersion: fields.sequence(['2.36.1-werw', '2.32.0']),
    temporary: fields.sequence([true, false, true, false, false]),
    taxationTypes: ['VAT', 'INCOME', 'PROFIT', 'SIMPLIFIED'],
    status: 'ACTIVE', //'SERVICE', //'BLOCKED',
    statusComment: 'Mock comment for current status.',
    statusDateAtUtc: '2020-04-29T00:00:00',
    //----------------------------------------------
    blockStartAtUtc: null, //'2020-05-29T10:00:00',
    blockStartComment: null,
    //'Lorem ipsum dolor sit amet, consectetuer adipiscing elit. Aenean commodo ligula eget dolor. Aenean massa.',
    blockEndAtUtc: null, //'2020-07-29T18:00:00',
    blockEndComment: null,
    //'Lorem ipsum dolor sit amet, consectetuer adipiscing elit. Aenean commodo ligula eget dolor. Aenean massa.',
    //----------------------------------------------
    cashRegisterModelName: 'Model 0000000001',
    cashRegisterModelTaxNumber: (ctx) => 15500 + ctx.index,
    cashRegisterFactoryNumber: '122345543',
    storeTaxAuthority: 'Tax authority 2',
    fiscalDriverVersion: '1.41.0',
    storeStatus: STORE_STATUSES[0],
    companyTaxNumber: '0200000711',
    companyName: '"LC" İSTEHSAL-KOMMERSİYA',
    companyStatus: COMPANIES_STATUSES[1],
    os: 'Windows NT 6.1 (x86)',
    pingDateAtUtc: '2020-02-17T11:27:11.996063',
    deviceFactoryNumber: (ctx) => `73237536478${ctx.index}`,
    certificateEndDateAtUtc: '2020-02-17T11:27:11.996063',
    registeredDateAtUtc: '2020-01-09T11:27:11.996063',
    auditLastDateAtUtc: '2020-02-07T11:27:11.996063',
    softwareVersion: '2.0.12',
    softwareVersionStatus: VersionStatus.OUTDATED,
    portable: true,
    cashBoxType: fields.sequence([
        CashBoxTypes.PHYSICAL,
        CashBoxTypes.ECOMM,
        CashBoxTypes.VIRTUAL,
        CashBoxTypes.SOFTWARE,
    ]),
    storeWebSite: 'https://storeWebSite.com',
    storeMainActivity: fields.sequence(Object.values(StoreMainActivity)),
    storeMiddleActivity: fields.sequence(Object.values(StoreMiddleActivity)),
    storeLowerActivity: fields.sequence(Object.values(StoreLowerActivity)),
})
    .setFieldFactory('companyName', (ctx) => `Company${ctx.inject('companyTaxNumber')} name`)
    .setFieldFactory('operatorName', (ctx) => `Operator${ctx.inject('operatorTaxNumber')} name`)
    .setFieldFactory('storeName', (ctx) => `Store${ctx.inject('storeTaxNumber')} name`)
    .factory();

export const cashBoxLastDocumentFactory = factoryT<CashBoxLastDocument>({
    docTypeId: 20,
    createdAtUtc: '2020-01-09T11:27:11.996063',
    insertedDateAtUtc: '2020-01-09T11:27:11.996063',
    sum: 123.555,
    docNumber: (ctx) => ctx.index,
    globalDocNumber: (ctx) => 1000 + ctx.index,
});

export const TurnoversByPeriodFactory = factoryT<RegistryTurnoversByPeriodDto>({
    date: (ctx) => `${moment(new Date(2000 + ctx.index, ctx.index - 1, 5 + ctx.index)).format('DD-MM-YYYY')}`,
    ...turnoversPartialDtoFactory.item(),
    currency: Currency.AZN,
    vatSum: 20390,
    turnover: 4000000,
});

export const CashBoxStatusFactory = factoryT<CashBoxStatusDto>({
    status: fields.sequence(Object.values(CASH_BOXES_STATUSES)),
    dateAtUtc: '2020-01-09T11:27:11.996063',
    duration: 63072000,
    userName: 'Ivanov Ivan',
    comment: 'Some comment on status change',
});

export const CashBoxTimeDeltaFactory = factoryT<CashBoxTimeDeltaDto>({
    validTime: fields.sequence([false, true, true, false, false]),
    dateAtUtc: '2020-01-09T11:27:11.996063',
    timeDelta: fields.sequence([null, 57, 452, 0, -7643, 0, 0, 0, 0, null]),
    duration: fields.sequence([null, 57093, 452786, 0, 76433]),
});

export const CashBoxStateFactory = factoryT<CashBoxStateDto>({
    state: fields.sequence(['ONLINE', 'OFFLINE']),
    dateAtUtc: '2020-01-09T11:27:11.996063',
    duration: fields.sequence([63072000, 13678, null]),
});

export const CashBoxAuditFactory = factoryT<AuditDto>({
    status: fields.sequence(['SUCCESS', 'FAILED']),
    date: '2020-01-09T11:27:11.996063',
    details:
        'details, details, details details! details Details! details Details! details Details! and more and more and more',
});

export const CashBoxAuditHistoryItemFactory = factoryT<CashBoxAuditHistoryItemDto>({
    audits: fields.sequence([CashBoxAuditFactory.list({ count: 3 }), null, []]),
    year: 2020,
    quarter: fields.sequence([4, 1, 3, 2]),
});

export const CashBoxReplacementFactory = factoryT<CashBoxReplacementDto>({
    status: fields.sequence([...CASH_BOXES_STATUSES]),
    taxNumber: (ctx) => `7323432${ctx.index}`,
    cashRegisterFactoryNumber: '122345543',
    cashRegisterModelName: 'Model 0000000001',
    registrationDateAtUtc: '2020-01-09T11:27:11.996063',
    temporary: fields.sequence([true, false]),
    certificateExpirationDateAtUtc: '2020-01-09T11:27:11.996063',
});

export const CashBoxAlertsFactory = factoryT<CashBoxAlertsDto>({
    month: fields.sequence(['2021-05', '2021-06', '2021-07', '2021-08']),
    minor: fields.sequence([2, 0, 5, 4]),
    major: fields.sequence([2, 0, 5, 4, 35]),
    normal: fields.sequence([2, 0, 5, 48]),
    count: fields.sequence([2, 0, 0, 7777]),
});

export const cashBoxOnMapFactory = factoryT<CashBoxPoint>({
    taxNumber: (ctx) => `10494226311${ctx.index}`.substring(0, 15),
    lat: 0,
    lon: 0,
});

export const cashBoxDetailsForMapFactory = factoryT<CashBoxDetailsForMap>({
    taxNumber: (ctx) => `10494226311${ctx.index}`.substring(0, 15),
    online: Math.random() < 0.5,
    gpsUpdatedDateAtUtc: '2020-02-03T11:27:11.996063',
});

export const cashBoxLocationDataFactory = factoryT<CashboxLocationData>({
    lat: 40.667,
    lon: 48.5,
    gpsUpdatedAtUtc: '2020-01-09T11:27:11.996063',
    storeLat: 41,
    storeLon: 48.999,
});

export const locationsListItemFactory = factoryT<LocationsListItem>({
    lat: (ctx) => 40.4 - Math.random() / 100 + ctx.index / 100,
    lon: (ctx) => 49.84 + Math.random() / 100 - ctx.index / 100,
    insertedDateAtUtc: (ctx) => moment('2020-01-09T11:27:11.996063').subtract(ctx.index, 'days').format(),
});

export const checksumsHistoryItemFactory = factoryT<CheckSumsHistoryItemDto>({
    softwareVersion: (ctx) => `1.03.${ctx.index}`,
    softwareVersionStatus: fields.sequence([
        VersionStatus.ATTESTATION,
        VersionStatus.ACTIVE,
        VersionStatus.OUTDATED,
        VersionStatus.ELIMINATED,
        null,
    ]),
    insertedDateAtUtc: '2023-07-08T15:19:42.097694',
    updatedDateAtUtc: '2022-07-08T15:19:42.097694',
    checksum: (ctx) =>
        `${ctx.index}bc4221458d3eda7b2b0bdcc753578a4c3c743e18997b0bfa6769210bc5cd6e3af216f37ad3434e38014168c32690375ff5a470d63128f623978b608183aa4979`,
    fiscalDriverVersion: (ctx) => `3.${ctx.index}.0`,
    firmwareVersion: (ctx) => `1.23.${ctx.index}`,
});
